import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AnnotationPanel = ({ 
  annotations, 
  enrichedComponents = [],
  onAddAnnotation, 
  onDeleteAnnotation, 
  onEditAnnotation,
  onVoteAnnotation = () => {},
  onSelectAnnotationDetail,
  isVisible, 
  onToggle,
  selectedComponent,
  currentUser
}) => {
  const [newAnnotation, setNewAnnotation] = useState('');
  const [isAddingAnnotation, setIsAddingAnnotation] = useState(false);
  
  // Inline editing states
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  // Symmetrical Collaborative Annotations Filtering:
  const filteredAnnotations = annotations?.filter(ann => {
    const annCompId = ann.componentId || ann.component_id;
    const annCompName = (ann.componentName || ann.component_name || ann.component?.name || '').toLowerCase().trim();
    
    if (selectedComponent) {
      const selectedName = (selectedComponent.name || selectedComponent.id || '').toLowerCase().trim();
      const targetUuid = selectedComponent.dbComponentId || selectedComponent.id;
      
      // Symmetrical Name & UUID Combo Matcher:
      // If the selected component's name or UUID matches the annotation's component name or UUID, they display!
      const matchesUuid = targetUuid && annCompId && annCompId.toString().toLowerCase() === targetUuid.toString().toLowerCase();
      
      const matchesName = selectedName && annCompName && (
        selectedName === annCompName ||
        selectedName.includes(annCompName) ||
        annCompName.includes(selectedName)
      );
      
      return matchesUuid || matchesName;
    }
    // Symmetrically show ALL annotations when no part is selected!
    return true;
  }) || [];

  // Symmetrical Privacy-Protecting Username Formatter: Excludes raw email addresses!
  const getUserDisplayName = (ann) => {
    const fName = ann?.user?.firstName || ann?.user?.first_name || '';
    const lName = ann?.user?.lastName || ann?.user?.last_name || '';
    
    if (fName) {
      const lastInitial = lName ? ` ${lName[0].toUpperCase()}.` : '';
      return `${fName}${lastInitial}`;
    }
    
    const email = ann?.user?.email || '';
    if (email) {
      const localPart = email.split('@')[0];
      const nameParts = localPart.split(/[\._-]/);
      const displayFirst = nameParts[0] ? nameParts[0][0].toUpperCase() + nameParts[0].slice(1) : 'User';
      const displayLast = nameParts[1] ? ` ${nameParts[1][0].toUpperCase()}.` : '';
      return `${displayFirst}${displayLast}`;
    }
    
    return 'User';
  };

  const handleAddAnnotation = () => {
    if (newAnnotation?.trim()) {
      onAddAnnotation({
        text: newAnnotation,
        componentId: selectedComponent?.dbComponentId || null, // Pass the true database UUID!
        componentName: selectedComponent ? (selectedComponent.name || selectedComponent.id) : null, // Pass active component name!
        position: { x: Math.random() * 10, y: Math.random() * 10, z: Math.random() * 10 }
      });
      setNewAnnotation('');
      setIsAddingAnnotation(false);
    }
  };

  const handleStartEdit = (annotation) => {
    setEditingId(annotation.id);
    setEditText(annotation.text);
  };

  const handleSaveEdit = (id) => {
    if (editText?.trim() && onEditAnnotation) {
      onEditAnnotation(id, editText);
      setEditingId(null);
      setEditText('');
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      month: 'short',
      day: 'numeric'
    })?.format(date);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="absolute bottom-20 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] bg-white/[0.88] backdrop-blur-md rounded-lg border border-border shadow-floating max-h-[calc(100vh-14rem)] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border shrink-0 bg-gray-50/50">
        <div className="flex items-center space-x-2">
          <Icon name="MessageSquare" size={20} className="text-primary" />
          <h3 className="text-sm font-bold text-foreground">Collaborative Annotations</h3>
          <span className="text-xs font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
            {filteredAnnotations?.length || 0}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="X"
          onClick={onToggle}
          className="w-8 h-8 rounded-full"
        />
      </div>

      {/* Add Annotation Form */}
      <div className="p-4 border-b border-border bg-white shrink-0">
        {!isAddingAnnotation ? (
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            onClick={() => setIsAddingAnnotation(true)}
            className="w-full text-xs font-bold uppercase py-2 cursor-pointer"
          >
            Add Annotation
          </Button>
        ) : (
          <div className="space-y-3">
            {selectedComponent && (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 text-blue-700 rounded-md border border-blue-100 text-xs font-medium">
                <Icon name="MapPin" size={13} className="shrink-0" />
                <span>Attaching to part: <strong>{selectedComponent.name || selectedComponent.customName}</strong></span>
              </div>
            )}
            <textarea
              placeholder={selectedComponent ? "Comment on this selected part..." : "Comment on this 3D model..."}
              value={newAnnotation}
              onChange={(e) => setNewAnnotation(e?.target?.value)}
              rows={4}
              className="w-full text-xs border border-gray-200 rounded-lg p-2.5 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none font-sans"
            />
            <div className="flex space-x-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleAddAnnotation}
                disabled={!newAnnotation?.trim()}
                className="flex-1 text-xs py-1.5 cursor-pointer"
              >
                Add Comment
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsAddingAnnotation(false);
                  setNewAnnotation('');
                }}
                className="flex-1 text-xs py-1.5 cursor-pointer"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Annotations List */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50/30">
        {filteredAnnotations?.length === 0 ? (
          <div className="py-12 text-center">
            <Icon name="MessageSquare" size={36} className="text-muted-foreground/60 mx-auto mb-3" />
            <p className="text-xs font-bold text-muted-foreground">No annotations yet</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Be the first to click "Add Annotation" and start collaborating!
            </p>
          </div>
        ) : (
          <div className="space-y-3.5">
            {filteredAnnotations?.map((annotation) => {
              const isOwner = currentUser && (annotation.userId === currentUser.id || annotation.user?.id === currentUser.id);
              const isEditing = editingId === annotation.id;

              return (
                <div 
                  key={annotation?.id} 
                  onDoubleClick={() => onSelectAnnotationDetail && onSelectAnnotationDetail(annotation)}
                  className="p-3 bg-white rounded-lg border border-gray-150 shadow-sm transition hover:shadow cursor-pointer select-none"
                  title="Double-click to view full details"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center space-x-2 min-w-0">
                      <div className="w-6 h-6 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center shrink-0">
                        <Icon name="User" size={12} color="white" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-gray-800 truncate">
                          {getUserDisplayName(annotation)}
                        </p>
                        <p className="text-[10px] text-muted-foreground">{formatTime(annotation?.createdAt || annotation?.created_at)}</p>
                      </div>
                    </div>

                    {isOwner && (
                      <div className="flex items-center gap-1 shrink-0">
                        {!isEditing && (
                          <button
                            onClick={() => handleStartEdit(annotation)}
                            className="p-1 text-gray-400 hover:text-primary hover:bg-gray-100 rounded transition cursor-pointer"
                            title="Edit Annotation"
                          >
                            <Icon name="Edit" size={13} />
                          </button>
                        )}
                        <button
                          onClick={() => onDeleteAnnotation(annotation?.id)}
                          className="p-1 text-gray-400 hover:text-destructive hover:bg-red-50 rounded transition cursor-pointer"
                          title="Delete Annotation"
                        >
                          <Icon name="Trash2" size={13} />
                        </button>
                      </div>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="space-y-2 mt-1.5">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={3}
                        className="w-full text-xs border border-gray-200 rounded-lg p-2 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none font-sans"
                      />
                      <div className="flex space-x-1.5 justify-end">
                        <Button
                          variant="default"
                          size="xs"
                          onClick={() => handleSaveEdit(annotation.id)}
                          disabled={!editText?.trim()}
                          className="text-[11px] py-1 px-2 cursor-pointer"
                        >
                          Save
                        </Button>
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => setEditingId(null)}
                          className="text-[11px] py-1 px-2 cursor-pointer"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="w-full max-w-full overflow-hidden mt-1 text-left">
                        <p className="text-xs text-gray-700 leading-relaxed break-words font-medium whitespace-pre-wrap font-sans">
                          {annotation?.text}
                        </p>
                      </div>

                      {/* Component Association Tag */}
                      {annotation?.component && (
                        <div className="inline-flex items-center gap-1 mt-2 px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-bold">
                          <Icon name="Cpu" size={11} />
                          <span>Part: {annotation.component.name || annotation.component.customName}</span>
                        </div>
                      )}

                      {/* Helpfulness Voting Controls */}
                      <div className="flex items-center gap-3 mt-3 pt-2 border-t border-gray-100 text-[10px] text-gray-500">
                        <span className="font-medium">Find this useful?</span>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Avoid triggering double-click detail
                              onVoteAnnotation(annotation.id, 'up');
                            }}
                            className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-green-50 hover:text-green-600 transition cursor-pointer"
                            title="Helpful (Thumbs Up)"
                          >
                            <Icon name="ThumbsUp" size={11} />
                            <span>{annotation.thumbsUp || annotation.thumbs_up || 0}</span>
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onVoteAnnotation(annotation.id, 'down');
                            }}
                            className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-red-50 hover:text-red-600 transition cursor-pointer"
                            title="Not Helpful (Thumbs Down)"
                          >
                            <Icon name="ThumbsDown" size={11} />
                            <span>{annotation.thumbsDown || annotation.thumbs_down || 0}</span>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnotationPanel;
